import { Input, Select } from 'antd';
import React, { memo, useCallback, useEffect } from 'react';

export type VoiceFormData = {
  voiceName: string;
  gender: number;
  language?: string;
};
interface VoiceFormProps {
  onChange: (data: VoiceFormData) => void;
}
import { usePartial } from '@/hooks/usePartial';
import { useTranslationConfig } from '@/hooks/useTranslationConfig';
import selectStyles from '@/styles/select.module.css';
import { t } from '@/utils/helper/intl';

import NoIcon from '../../icon/no';
import YesIcon from '../../icon/yes';
import styles from '../../style.module.scss';

export const defaultVoiceData = {
  voiceName: '',
  gender: 0,
  language: '',
};

const VoiceName: React.FC<{
  name: string;
  onChange: (name: string) => void;
}> = memo((props) => {
  return (
    <div className="flex-1 flex flex-col justify-start gap-[8px]">
      <span className="shrink-0 w-[94px] box-content inline-block  text-[14px] font-[500]">
        {t('boom.0.9.createvoi.name')} :{' '}
      </span>
      <div className="flex items-center  border border-[#C5C5C5] rounded-[8px] p-[10px] gap-[16px]">
        <Input
          value={props.name}
          className={styles['upload-voice-input']}
          style={{ height: 32, resize: 'none', fontSize: 14 }}
          maxLength={20}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
        />
        <span className="font-number text-[#a3a3a3] text-[14px] tracking-[0.24px] font-[400] pr-[12px]">
          {props.name.length}/20
        </span>
      </div>
    </div>
  );
});

const VoiceGender: React.FC<{ gender: number; onChange: (gender: number) => void }> =
  memo((props) => {
    const checkboxData = [
      {
        label: t('merror.createava.male'),
        value: 1,
      },
      {
        label: t('merror.createava.female'),
        value: 2,
      },
    ];
    return (
      <div className="flex-1  flex flex-col justify-start gap-[8px]">
        <span className=" w-[90px] box-content  inline-block text-[14px] font-[500]">
          {t('merror.createava.gender')} :{' '}
        </span>
        <div className="flex items-center border border-[var(--app-border)] rounded-[8px] px-[22px] py-[10px] bg-[var(--app-bg-input)]">
          <div className="flex gap-[32px] h-[32px]">
            {checkboxData.map((item) => {
              return (
                <div
                  key={item.value}
                  className="flex items-center gap-[6px] cursor-pointer transition-all "
                  onClick={() => {
                    props.onChange(item.value);
                  }}
                >
                  <div
                    className={`transition-colors w-[16px] h-[16px] border border-[var(--app-border-strong)] rounded-full ${props.gender === item.value ? 'bg-[#5661F1]' : ''}`}
                  ></div>
                  <span className="text-[12px] font-[400] text-app-secondary">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  });

const VoiceLanguage: React.FC<{
  language?: string;
  onChange: (language: string) => void;
  options: { label: string; value: string }[];
}> = memo((props) => {
  return (
    <div className="flex flex-col justify-start items-start gap-[8px]">
      <span className=" w-[90px] box-content  inline-block text-[14px] font-[500]">
        {t('ff71243806c894d378a25f2cc98023de')} :{' '}
      </span>
      <div className={`${selectStyles.select}`}>
        <Select
          style={{ width: 324, height: 32 }}
          options={props.options}
          placeholder={t('d17b808544adb75ed3d855ea5e821d58')}
          value={props.language || undefined}
          onChange={(value) => props.onChange(value)}
          classNames={{
            popup: {
              root: selectStyles.popup,
            },
          }}
        />
      </div>
    </div>
  );
});

const VoiceForm: React.FC<VoiceFormProps> = memo(({ onChange }) => {
  const [voiceData, setVoiceData] = usePartial<VoiceFormData>(defaultVoiceData);
  const [_, targetLang] = useTranslationConfig(t);

  const tipData = [
    t('merror.createava.clonevoice.solo'),
    t('merror.createava.clonevoice.4'),
    t('merror.createava.clonevoice.2'),
    t('merror.createava.clonevoice.5'),
    t('merror.createava.clonevoice.3'),
    t('merror.createava.clonevoice.6'),
  ];
  useEffect(() => {
    onChange(voiceData);
  }, [voiceData]);
  const changeName = useCallback((voiceName: string) => {
    setVoiceData({ voiceName });
  }, []);
  const changeGender = useCallback((gender: number) => {
    setVoiceData({ gender });
  }, []);
  const changeLanguage = useCallback((language: string) => {
    setVoiceData({ language });
  }, []);

  return (
    <div className="py-[14px] flex flex-col gap-[20px]">
      <div className=" flex justify-end gap-[10px]">
        <VoiceName name={voiceData.voiceName} onChange={changeName} />
        <VoiceGender gender={voiceData.gender} onChange={changeGender} />
      </div>
      <VoiceLanguage
        language={voiceData.language}
        onChange={changeLanguage}
        options={targetLang}
      />
      <div className="p-[10px] flex items-center justify-center">
        <div className=" w-[408px] flex flex-wrap gap-y-[2px]">
          {tipData.map((item, index) => {
            return (
              <div key={index} className="flex w-1/2  gap-[8px]">
                {index % 2 === 0 ? <YesIcon /> : <NoIcon />}
                <span className="text-[12px] leading-[24px] font-[400]">{item}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default VoiceForm;
